import React, {useContext, useEffect, useMemo, useState} from 'react';
import ReactDOM from 'react-dom';
import styles from './TeleopSessionCompleteModal.module.css';
import closeButtonHoverImg from '../../static_files/closeButtonHover.svg';
import closeButtonImg from '../../static_files/closeButton.svg';
import pointSvg from '../../static_files/point icon.svg';

import {PRISMAX_BACKEND_Control_URL} from '../../configs/config';
import {SwapMainAppContext} from "../../context/SwapMainAppContext";

function minsSecParts(h) {
    const n = Number(h);
    if (!Number.isFinite(n)) return {mins: '00', secs: '00'};
    const totalSecs = Math.max(0, Math.floor(n * 3600));
    const mins = String(Math.floor(totalSecs / 60)).padStart(2, '0');
    const secs = String(totalSecs % 60).padStart(2, '0');
    return {mins, secs};
}

function pickMoves(controlled_result) {
    if (!controlled_result) return {leftToRight: 0, rightToLeft: 0};
    let obj = controlled_result;
    if (typeof obj === 'string') {
        try {
            obj = JSON.parse(obj);
        } catch {
            return {leftToRight: 0, rightToLeft: 0};
        }
    }
    if (!obj || typeof obj !== 'object') return {leftToRight: 0, rightToLeft: 0};
    const ids = ['8001', '8002', '8003', '8004', '8005'];
    let key = ids.find(k => obj[k] != null);
    if (!key) {
        const ks = Object.keys(obj);
        key = ks.length ? ks[0] : null;
    }
    const entry = key ? obj[key] : null;
    const start = entry?.start || {};
    const end = entry?.end || {};

    const leftToRight = Math.max(0, Number(end.right ?? 0) - Number(start.right ?? 0));
    const rightToLeft = Math.max(0, Number(end.left ?? 0) - Number(start.left ?? 0));

    return {leftToRight, rightToLeft};
}

function TeleopSessionCompleteModal({onClose, controlToken}) {
    const [closeButtonHover, setCloseButtonHover] = useState(false);
    const [loading, setLoading] = useState(true);
    const [payload, setPayload] = useState(null);

    const {gatewayToken, showNotification, userId} = useContext(SwapMainAppContext);

    useEffect(() => {
        let mounted = true;
        const start = Date.now();
        const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

        const poll = async () => {
            await sleep(5000);
            while (mounted) {
                const elapsed = Date.now() - start;
                if (elapsed >= 120_000) {
                    showNotification('error', 'Network error', '20000'); // TODO: 外层需要时可改成专门的超时UI
                    onClose();
                    return;
                }
                try {
                    const res = await fetch(
                        `${PRISMAX_BACKEND_Control_URL}/fetch_tele_op_session_complete_status`,
                        {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                Authorization: `Bearer ${gatewayToken}`,
                            },
                            body: JSON.stringify({userId, controlToken}),
                        }
                    );

                    if (res.status === 404) {
                        onClose();
                        return;
                    }

                    const json = await res.json();

                    if (json?.status === 'not_finished') {
                        await sleep(3000);
                        continue;
                    }

                    if (mounted) {
                        setPayload(json);
                        setLoading(false);
                    }
                    return;
                } catch {
                    await sleep(3000);
                }
            }
        };

        poll();
        return () => {
            mounted = false;
        };
    }, [userId, controlToken, gatewayToken, onClose, showNotification]);

    const points = payload?.reward_points ?? 0;
    const {mins, secs} = useMemo(() => minsSecParts(payload?.controlled_hours), [payload]);
    const {leftToRight, rightToLeft} = useMemo(() => pickMoves(payload?.controlled_result), [payload]);

    return ReactDOM.createPortal(
        <div
            className={styles.modalOverlay}
            onClick={(e) => {
                if (e.target === e.currentTarget) onClose();
            }}
        >
            <div className={styles.modalContainer}>
                <div className={styles.headerRow}>
                    <img
                        onMouseEnter={() => setCloseButtonHover(true)}
                        onMouseLeave={() => setCloseButtonHover(false)}
                        src={closeButtonHover ? closeButtonHoverImg : closeButtonImg}
                        className={styles.closeBtn}
                        onClick={onClose}
                        alt="x"
                    />
                </div>

                <div className={styles.content}>
                    <div className={styles.title}>Session Completed!</div>
                    <div className={styles.subtitle}>
                        Here is a recap of the points accumulated and of the hours contributed
                        <br className={styles.br}/>
                        throughout your sessions.
                    </div>

                    <div className={styles.topRow}>
                        <div className={styles.metricCard}>
                            <div className={styles.metricTitle}>Prisma Points</div>
                            <div className={styles.metricValue}>
                                <img className={styles.pointsIcon} src={pointSvg} alt="points" />
                                {loading ? (
                                    <span className={`${styles.skeletonBox} ${styles.skeletonValueLarge}`}/>
                                ) : (
                                    <>+{points}</>
                                )}
                            </div>
                        </div>

                        <div className={styles.metricCard}>
                            <div className={styles.metricTitle}>Tele-Op Hours Contributed</div>
                            <div className={styles.metricValue2}>
                                {loading ? (
                                    <span className={`${styles.skeletonBox} ${styles.skeletonValueMedium}`}/>
                                ) : (
                                    <>
                                        +{mins} <span className={styles.unit}>mins</span> : {secs}
                                        <span className={styles.unit}>sec</span>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className={styles.movementRow}>
                        <div className={styles.movementCard}>
                            <div className={styles.movementTitle}>
                                Successfully Moved Left-Right <span className={styles.arrow} aria-hidden>→</span>
                            </div>
                            <div className={styles.movementWrapper}>
                                <div className={styles.movementCount}>
                                    {loading ? (
                                        <span className={`${styles.skeletonBox} ${styles.skeletonCount}`}/>
                                    ) : (
                                        <>{leftToRight}</>
                                    )}
                                </div>
                                <div className={styles.smallLabel}>Objectives</div>
                            </div>
                        </div>

                        <div className={styles.divider}/>

                        <div className={styles.movementCard}>
                            <div className={styles.movementTitle}>
                                Successfully Moved Right-Left <span className={styles.arrow} aria-hidden>←</span>
                            </div>
                            <div className={styles.movementWrapper}>
                                <div className={styles.movementCount}>
                                    {loading ? (
                                        <span className={`${styles.skeletonBox} ${styles.skeletonCount}`}/>
                                    ) : (
                                        <>{rightToLeft}</>
                                    )}
                                </div>
                                <div className={styles.smallLabel}>Objectives</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>,
        document.getElementById('root')
    );
}

export default TeleopSessionCompleteModal;
